import React from "react";
import { format, parseISO } from "date-fns";
import { FinancialAccount } from "@common/types/financialAccount";
import { FinancialConnection } from "@common/types/financialConnection";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { isPlaidConnection } from "@common/helpers/financialConnection";
import { isIntegrationConnection } from "@common/helpers/integrationConnection";
import { useEnablePlaidStatementsMutation } from "@mutations/plaidConnection";
import { usePlaidStatementsQualified } from "@hooks/usePlaidStatementsQualified";
import { timeAgo } from "@helpers/date";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import {
    StatementSyncState,
    getConnectionStatementSyncState,
} from "@components/accounts/views/accountStatementSync.helpers";
import { AccountDetailsField } from "./AccountDetailsField";

interface AccountStatementsInfoProps {
    account: FinancialAccount;
    connection: FinancialConnection | IntegrationConnection;
}

export const AccountStatementsInfo: React.FC<AccountStatementsInfoProps> = ({
    account,
    connection,
}) => {
    const isPlaidStatementsQualified = usePlaidStatementsQualified();

    const { state, lastStatementDate, statementsSyncedAt } =
        getConnectionStatementSyncState({
            account,
            connection,
            isPlaidStatementsQualified,
        });

    if (!state) {
        return null;
    }

    return (
        <>
            <AccountDetailsField title="Statement sync">
                <StatementSyncContent
                    state={state}
                    connection={connection}
                    statementsSyncedAt={statementsSyncedAt}
                />
            </AccountDetailsField>

            {state === "synced-found" && lastStatementDate && (
                <AccountDetailsField title="Last statement pulled">
                    {format(
                        parseISO(lastStatementDate),
                        fullDateFullYearFormat,
                    )}
                </AccountDetailsField>
            )}
        </>
    );
};

const StatementSyncContent: React.FC<{
    readonly state: StatementSyncState;
    readonly connection: FinancialConnection | IntegrationConnection;
    readonly statementsSyncedAt: Date | null;
}> = ({ state, connection, statementsSyncedAt }) => {
    switch (state) {
        case "not-qualified":
            return <>Not included in plan</>;
        case "not-supported":
            return <>Not supported</>;
        case "supported-not-enabled":
            return !isIntegrationConnection(connection) &&
                isPlaidConnection(connection) ? (
                <EnablePlaidStatementsButton connectionId={connection.id} />
            ) : null;
        case "enabled-not-synced":
            return <>Syncing</>;
        case "synced-none-found":
            return <>Syncing, no statements found</>;
        case "synced-found":
            return (
                <>
                    Synced{" "}
                    {statementsSyncedAt ? timeAgo(statementsSyncedAt) : ""}
                </>
            );
    }
};

const EnablePlaidStatementsButton: React.FC<{
    readonly connectionId: number;
}> = ({ connectionId }) => {
    const enableStatements = useEnablePlaidStatementsMutation({
        id: connectionId,
    });

    return (
        <ButtonWithLoader
            variant="primary"
            size="sm"
            loading={enableStatements.isPending}
            onClick={() => enableStatements.mutate()}
        >
            Enable in Plaid
        </ButtonWithLoader>
    );
};
